import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Analytics from "../assets/icons/Analytics.svg";
import DocumentIcon from "../assets/icons/Document-text.svg";
import HomeIcon from "../assets/icons/Home.svg";
import LanguageIcon from "../assets/icons/Language.svg";
import Settings from "../assets/icons/Settings.svg";
import LogoutIcon from "../assets/icons/logout.svg";
import PromotionalIcon from "../assets/icons/information.svg";
import { useAuth } from "../contexts/AuthContext";
import { isImageUrl } from "../utils/isImageUrl";
import apiService from "../services/apiService";
import FilesForm from "./forms/FilesForm";
import SettingsForm from "./forms/SettingsForm";
import PromotionalPopupForm from "./forms/PromotionalPopupForm";
import HolomenuTitle from "./holomenuTitle";
import LazyImage from "./lazyImage";

const HeaderSection = (props) => {
  const { restaurantLogo, auth, logout } = useAuth();
  const [isSettingFormOpen, setIsSettingFormOpen] = useState(false);
  const [isFilesFormOpen, setIsFilesFormOpen] = useState(false);
  const [isPromotionalPopupFormOpen, setIsPromotionalPopupFormOpen] = useState(false);
  const [promotionalPopupId, setPromotionalPopupId] = useState(null);

  const isAdmin = auth.role !== "user";

  const handleToggglingOpenSettingForm = () =>
    setIsSettingFormOpen(!isSettingFormOpen);

  const handleTogglingOpenFilesForm = () => setIsFilesFormOpen((prev) => !prev);

  const handleTogglingOpenPromotionalPopupForm = async () => {
    if (!isPromotionalPopupFormOpen) {
      // When opening, fetch the existing popup ID
      try {
        const response = await apiService.get("/promotional-popups");
        const popups = response?.data?.data || [];
        // Since there's only one popup per restaurant, get the first one
        setPromotionalPopupId(popups[0]?.id || null);
      } catch (error) {
        console.error("Error fetching promotional popup:", error);
        setPromotionalPopupId(null);
      }
    }
    setIsPromotionalPopupFormOpen((prev) => !prev);
  };

  const renderRestaurantLogo = () => {
    const isLogoUrl = isImageUrl(restaurantLogo);
    if (isLogoUrl)
      return (
        <>
          <LazyImage
            src={restaurantLogo}
            alt={auth.user.restaurant.restaurant_name}
            sx={{ height: "46px", filter: "brightness(0) invert(1)" }}
          />
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontWeight: "bold",
              fontSize: {
                xs: "3rem",
                sm: "4rem",
              },
              letterSpacing: "0.1rem",
              textAlign: "center",
              zIndex: 1,
              textTransform: "capitalize",
              position: "absolute",
              bottom: { xs: "-6px", md: "-8px" },
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "8px",
            }}
          >
            {auth?.user?.restaurant?.restaurant_name.split("|")[1]}
          </Typography>
        </>
      );
    else
      return (
        <HolomenuTitle
          title={auth?.user?.restaurant?.restaurant_name}
          style={{
            height: "34px",
            fontSize: { xs: "16px", md: "34px" },
            mb: 0,
          }}
        />
      );
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        paddingY: 2,
        width: "100%",
      }}
    >
      {/* Left Side - Home Button */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
        <HomeButton isAdmin={isAdmin} />
      </Box>

      {/* Center - Logo */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flex: 2,
          position: "relative",
        }}
      >
        {renderRestaurantLogo()}
      </Box>

      {/* Right Side - Language Dropdown IconButton */}
      <Box
        sx={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}
      >
        {auth.role === "superadmin" && <SuperAdminButton />}
        {isAdmin && (
          <>
            <PromotionalPopupButton onClick={handleTogglingOpenPromotionalPopupForm} />
            <FilesDataButton onClick={handleTogglingOpenFilesForm} />
            <AnaltyicsButton />
            <SettingsButton onClick={handleToggglingOpenSettingForm} />
            <PromotionalPopupForm
              open={isPromotionalPopupFormOpen}
              onClose={handleTogglingOpenPromotionalPopupForm}
              onSave={() => {}}
              popupId={promotionalPopupId}
            />
            <SettingsForm
              open={isSettingFormOpen}
              onClose={handleToggglingOpenSettingForm}
            />
            <FilesForm
              open={isFilesFormOpen}
              onClose={handleTogglingOpenFilesForm}
            />
          </>
        )}
        <LanguageButton />
        {isAdmin && <LogoutButton onClick={logout} />}
      </Box>
    </Box>
  );
};

export default HeaderSection;

const PromotionalPopupButton = ({ onClick }) => {
  return (
    <IconButton onClick={onClick} className="admin-btn">
      <img src={PromotionalIcon} alt="Promotional Popup" width={24} height={24} />
    </IconButton>
  );
};

const FilesDataButton = ({ onClick }) => {
  return (
    <IconButton onClick={onClick} className="admin-btn">
      <img src={DocumentIcon} alt="My Icon" width={24} height={24} />
      {/* <DescriptionRoundedIcon /> */}
    </IconButton>
  );
};

const AnaltyicsButton = () => {
  const navigate = useNavigate();
  return (
    <IconButton onClick={() => navigate("/analytics")} className="admin-btn">
      <img src={Analytics} alt="My Icon" width={24} height={24} />
    </IconButton>
  );
};

const SuperAdminButton = () => {
  const navigate = useNavigate();
  return (
    <IconButton
      onClick={() => navigate("/super-admin")}
      className="admin-btn"
      title="Super admin"
      aria-label="Super admin"
    >
      <AdminPanelSettingsIcon sx={{ color: "white" }} />
    </IconButton>
  );
};

const SettingsButton = ({ onClick }) => {
  return (
    <IconButton onClick={onClick} className="admin-btn">
      <img src={Settings} alt="My Icon" width={24} height={24} />
    </IconButton>
  );
};

const LogoutButton = ({ onClick }) => {
  return (
    <IconButton onClick={onClick} className="admin-btn">
      <img src={LogoutIcon} alt="My Icon" width={24} height={24} />
    </IconButton>
  );
};

const HomeButton = ({ isAdmin }) => {
  const navigate = useNavigate();
  return (
    <IconButton onClick={() => navigate("/startup")} disabled={isAdmin}>
      <img src={HomeIcon} alt="My Icon" width={24} height={24} />
    </IconButton>
  );
};

const LanguageButton = ({}) => {
  const theme = useTheme();
  const { languages, selectedLanguage, changeLanguage } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageChange = (language) => {
    setAnchorEl(null);
    changeLanguage(language);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <IconButton
        aria-controls="language-menu"
        aria-haspopup="true"
        onClick={handleLanguageClick}
        color="inherit"
      >
        <img src={LanguageIcon} alt="My Icon" width={24} height={24} />
      </IconButton>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          sx: {
            width: "200px",
          },
        }}
      >
        {!languages.find((lang) => lang.language_name === "English") && (
          <MenuItem
            key={0}
            selected={selectedLanguage?.language_id === 0}
            onClick={() =>
              handleLanguageChange({ language_id: 0, language_name: "English" })
            }
            sx={{
              typography: "body1",
              color:
                selectedLanguage?.language_id === 0
                  ? theme.palette.primary.main
                  : "inherit",
            }}
          >
            English
          </MenuItem>
        )}
        {languages.map((lang) => (
          <MenuItem
            key={lang.language_id}
            selected={selectedLanguage?.language_id === lang.language_id}
            onClick={() => handleLanguageChange(lang)}
            sx={{
              typography: "body1",
              color:
                selectedLanguage?.language_id === lang.language_id
                  ? theme.palette.primary.main
                  : "inherit",
            }}
          >
            {lang.language_name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
